import { ComponentPortal, Portal, TemplatePortal } from '@angular/cdk/portal';
import { ComponentFactoryResolver, Injectable, TemplateRef, Type, ViewContainerRef } from '@angular/core';
import { SideBarComponent, SidebarPosition } from '@m4u-angular/ui/layouts/layout/side-bar/side-bar.component';
import { Subject } from 'rxjs';
import { AppInjector } from '../injector/injector.module';
import { EnumSidebarStates, EnumWindowSizes } from './sysenums.enum';

@Injectable({
    providedIn: 'root'
})
export class SidebarService {
    _sidebars: Record<string, SideBarComponent> = {};
    public ViewContainerRef: ViewContainerRef;

    private _sidebarSubject = new Subject<{ SidebarName: string, State: EnumSidebarStates }>();
    readonly SidebarState = this._sidebarSubject.asObservable();

    constructor(private componentFactoryResolver: ComponentFactoryResolver) {
    }

    Show(sidebarName: string, title: string, content: Type<any> | TemplateRef<any>, width?: EnumWindowSizes, 
        componentOptions?: any, position? : SidebarPosition, className?: string) {
        let sidebarInstance = this._sidebars[sidebarName];
        if (!sidebarInstance || sidebarName === 'Filters') {
            sidebarInstance = this.CreateSidebarInstance();
            sidebarInstance.Name = sidebarName;
            sidebarInstance.Title = title;
            sidebarInstance.ContentPortal = this.ResolveContent(content);
            sidebarInstance.Width = width;
            sidebarInstance.SidebarComponentOptions = componentOptions;
            sidebarInstance.Position = position;
            sidebarInstance.Class = className;
            this._sidebars[sidebarName] = sidebarInstance;
        }
        
        sidebarInstance.SidebarState = EnumSidebarStates.Opened;
    }

    Hide(sidebarName: string): boolean {
        const sidebarInstance = this._sidebars[sidebarName];
        if (!sidebarInstance) return;

        if (sidebarInstance.SidebarPinned) return false;
        
        sidebarInstance.SidebarState = EnumSidebarStates.Closed;
        return true;
    }

    Close(sidebarName: string) {
        const sidebarInstance = this._sidebars[sidebarName];
        if (!sidebarInstance) return;

        sidebarInstance.SidebarState = EnumSidebarStates.Closed;
    }

    GetState(sidebarName: string) {
        const sidebarInstance = this._sidebars[sidebarName];
        return sidebarInstance && sidebarInstance.SidebarState;
    }

    OnStateChanged(sidebarName: string, state: EnumSidebarStates) {
        const sidebarInstance = this._sidebars[sidebarName];
        if (!sidebarInstance) return;

        if (sidebarInstance.SidebarState !== state) sidebarInstance.SidebarState = state;
        this._sidebarSubject.next({ SidebarName: sidebarName, State: state });
    }

    ClearSidebars() {
        Object.keys(this._sidebars).forEach(sidebarName => {
            this.Close(sidebarName);
        });
        this._sidebars = {};
    }

    DestroySidebar(sidebarName: string) {
        if (sidebarName && this._sidebars[sidebarName]) {
            this.Close(sidebarName);
            delete this._sidebars[sidebarName];
        }
    }

    private CreateSidebarInstance(): SideBarComponent {
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(SideBarComponent);
        const componentRef = this.ViewContainerRef.createComponent<any>(componentFactory);
        return componentRef.instance;
    }

    private ResolveContent(content: Type<any> | TemplateRef<any>): Portal<any> {
        return content instanceof TemplateRef
            ? new TemplatePortal(content, this.ViewContainerRef)
            : new ComponentPortal(content, this.ViewContainerRef, AppInjector, this.componentFactoryResolver);
    }
}